import { FileType } from '@/modules/apollo/constants';
import { isNil } from 'ramda';
import { match } from 'ts-pattern';

function createPathValidator(extensions: FileType[]) {
    // --- Common Components ---

    // 1. Restrictive segment name. Only allows alphanumeric, underscore, dash, and space.
    // This rule inherently prevents '..' from ever being a valid segment.
    const validSegmentName = '[a-zA-Z0-9_ -]+';

    // 2. Optional root for Windows (C:\) or Linux (/).
    const optionalRoot = '(?:(?:[a-zA-Z]:[\\\\\\/]|\\/))?';

    let pattern;

    if (extensions.length > 0) {
        // ---- Regex for paths ENDING with a specific extension ----
        const extensionsGroup = '(?:' + extensions.join('|') + ')';

        pattern =
            '^' +
            optionalRoot +
            // Zero or more directory segments.
            `(?:${validSegmentName}[\\\\\\/])*` +
            // The final file part: a valid name, a literal dot, and an extension.
            `${validSegmentName}\\.${extensionsGroup}` +
            '$';
    } else {
        // ---- General regex where NO DOTS are allowed anywhere ----
        pattern =
            '^' +
            optionalRoot +
            // Optional segments.
            `(?:${validSegmentName}(?:[\\\\\\/]+${validSegmentName})*)*` +
            // Optionally allow a trailing slash.
            '[\\\\\\/]?$';
    }

    // Return the new RegExp object, 'i' flag makes extensions case-insensitive.
    return new RegExp(pattern, 'i');
}

export const localRelativePathValidator = {
    message: (field: any, args: any) => {
        const fileTypes: FileType[] = args[0] || [];
        const extensions = fileTypes.map((ft: FileType) => match(ft).otherwise(() => ft));
        const examples = extensions
            .map(
                (e: string) =>
                    '"filename.' + e + '" or "someFolder/filename.' + e + '" or "someFolder\\filename.' + e + '"',
            )
            .join(' or ');

        return `The specific ${field} is not a valid path. Only alphanumeric characters, spaces, underscores, and hyphens are allowed. It should be for example ${examples}`;
    },
    validate: (value: any, args: any): boolean => {
        const fileTypes: FileType[] = args[0] || [];
        const extensions = fileTypes.map((ft: FileType) => match(ft).otherwise(() => ft));

        return createPathValidator(extensions).test(value);
    },
};
